/// <amd-module name="Environments/Providers/Kubernetes/KubernetesContainerExternal" />
import { IImageService } from "AzureDevOpsImageService";
import { KubeImage, ResourceErrorType } from "Environments/Providers/External/Kubernetes/Contracts/Contracts";
import { IKubeSummaryProps, KubeSummary } from "Environments/Providers/External/Kubernetes/WebUI/Common/KubeSummary";
import { AzureDevOpsKubeService, IKubeSummaryData } from "Environments/Providers/Kubernetes/AzureDevOpsKubeService";
import { EditKubernetesResourcePanel } from "Environments/Providers/Kubernetes/EditKubernetesResourcePanel";
import { ITaskAgentRestClient, TaskAgentClientName } from "Environments/Providers/Kubernetes/Generated/TaskAgentClient/TaskAgent";
import { KubernetesResource } from "Environments/Providers/Kubernetes/Generated/TaskAgentContracts/TaskAgent";
import { ImageLocationUtility } from "Environments/Providers/Kubernetes/ImageLocationUtility";
import { KubernetesBreadcrumbService } from "Environments/Providers/Kubernetes/KubernetesBreadcrumbService";
import { IKubernetesTelemetryService } from "Environments/Providers/Kubernetes/KubernetesTelemetryService";
import { LogContentReader } from "Environments/Providers/Kubernetes/LogContentReader";
import * as Resources from "Environments/Providers/Kubernetes/Resources";
import { IKubernetesResourceDetails, IPageHeaderData } from "Environments/Providers/Kubernetes/Types";
import { YamlReader } from "Environments/Providers/Kubernetes/YamlReader";
import { ResourceAccessDeniedZeroData, ResourceDeletedZeroData } from "Environments/Providers/Kubernetes/ZeroDataComponents";
import { IKubernetesDataService } from "PageData";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { safeParseJson } from "Utilities";
import { localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { getCurrentRoute, getRouteValues } from "VSS/Platform/Navigation";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";

declare type BreadcrumbItemType = { id: string, displayName: string, url: string };

class KubernetesContainerExternal extends VssComponent<IVssComponentProperties, {}>{
    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        // This is a HACK to initialize the 'IVssHistoryService' which adds 'sourcePage' property to the dataProvider request.
        // sourcePage property is needed to get project context in the data-provider (projectService.GetProject(rc))
        // Before making a data-provider call, fetchContributionsAsync() in 'Contribution.ts' fires 'dpqStarting' event.
        // This event is listened by IVssHistoryService in 'History.ts' via a subscription created during HistoryService initialization
        // Traditionally the historyService gets initialized by the eventListener of 'DOMContentLoaded' event in Context.ts as part of 'delayedServices'
        // But since here we are making the data-provider call from componentDidMount() itself, DOMContentLoaded is being fired at a later time.
        context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    public render(): React.ReactNode {
        const dataService = this.context.pageContext.getService<IKubernetesDataService>("IKubernetesDataService");
        const summaryData = dataService.getKubernetesSummaryData();
        const resourceDetails = dataService.getKubernetesResourceData();
        const resourceErrorType = dataService.getResourceErrorType();
        const { title, namespace, clusterName } = this._getPageHeaderData(resourceDetails && resourceDetails.resource);
        const kubeService = new AzureDevOpsKubeService(summaryData as IKubeSummaryData, this._loadDataFromServer);
        const imageService = this.context.pageContext.getService<IImageService>("IImageService");
        const currentRoute = getCurrentRoute(this.context.pageContext);
        const routeValues = getRouteValues(this.context.pageContext);
        const kubeNamespaces = safeParseJson(summaryData.namespaces);
        const onTitleBackClick = (() => {
            switch (currentRoute.routeId) {
                case "ms.vss-environments-web.kubernetes-summary-default-route":
                    return this._navigateToResourcesListFunc(routeValues.environmentId)
            }
        })();

        const props = {
            kubeService: kubeService,
            namespace: namespace,
            namespaces: kubeNamespaces && (kubeNamespaces.items || []).map(item => item.metadata && item.metadata.name || "").filter(n => n !== ""),
            clusterName: clusterName,
            clusterUrl: this._getClusterUrl(resourceDetails),
            title: title,
            markTTI: this._markTTI,
            getImageLocation: this._getImageLocation,
            imageService: imageService,
            onTitleBackClick: onTitleBackClick,
            getResourceErrorType: () => resourceErrorType,
            getResourceErrorActionComponent: this._getResourceErrorActionComponent,
            onViewChanged: (viewTree: BreadcrumbItemType[]) => this._handleViewChanged(viewTree),
            summaryPageHeaderCommandItems: (props?: any) => this._getHeaderBarItems(props && props.resourceErrorType, resourceDetails),
            getContentReaderComponent: this._getContentReaderComponent,
            telemetryService: this.context.pageContext.getService<IKubernetesTelemetryService>("IKubernetesTelemetryService")
        } as IKubeSummaryProps;

        // @ts-ignore The type of KubeSummary can't be determined because the referenced declare file cannot fully resolve the type.
        return <KubeSummary {...props} />;
    }

    public componentDidMount(): void {
        this._handleViewChanged([]);
        window.addEventListener("resize", this._onResizeHandler);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this._onResizeHandler);

        if (this._editor) {
            try {
                this._editor.dispose();
            } catch (e) { }

            this._editor = undefined;
        }
    }

    private _getResourceErrorActionComponent = (props?: any) => {
        if (props && props.resourceErrorType) {
            const resourceErrorType: ResourceErrorType = props.resourceErrorType as ResourceErrorType;
            switch (resourceErrorType) {
                case ResourceErrorType.AccessDenied:
                    const dataService = this.context.pageContext.getService<IKubernetesDataService>("IKubernetesDataService");
                    const resourceDetails: IKubernetesResourceDetails = dataService.getKubernetesResourceData() || {} as IKubernetesResourceDetails;

                    const isAzureSubscription = resourceDetails.kubernetesEndpoint && resourceDetails.kubernetesEndpoint.data.authorizationType === "AzureSubscription";
                    return (
                        <ResourceAccessDeniedZeroData
                            subText={isAzureSubscription ? Resources.AccessDeniedNewServiceAccountText : Resources.AccessDeniedServiceAccountText}
                            imagePath={this._getImageLocation(KubeImage.resourceAccessDenied) || ""}
                            resourceDetails={resourceDetails}
                            onActionClick={this._showValidateResourcePanel}
                        />
                    );

                case ResourceErrorType.Deleted:
                    return <ResourceDeletedZeroData imagePath={this._getImageLocation(KubeImage.resourceDeleted) || ""} />;
            }
        }

        return null;
    }

    private _showValidateResourcePanel = (resourceDetails: IKubernetesResourceDetails): void => {
        if (resourceDetails && resourceDetails.resource) {
            const resourceData = resourceDetails.resource;
            this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss) => {
                let envId = 0;
                let envName = "";
                if (resourceData && resourceData.environmentReference) {
                    envId = resourceData.environmentReference.id;
                    envName = resourceData.environmentReference.name;
                }

                return (
                    <EditKubernetesResourcePanel
                        environmentId={envId}
                        environmentName={envName}
                        onClose={() => onDismiss()}
                        resource={resourceData}
                        endpoint={resourceDetails.kubernetesEndpoint}
                        onEditComplete={() => {
                            const currentUrl = window.location.href;
                            FastPageSwitch(this.context.pageContext, currentUrl, false);
                        }}
                    />
                );
            });
        }
    }

    private _getHeaderBarItems(resourceErrorType?: ResourceErrorType, resourceDetails?: IKubernetesResourceDetails): IHeaderCommandBarItem[] | undefined {
        const resourceData = resourceDetails && resourceDetails.resource;
        let headerBarItems: IHeaderCommandBarItem[] | undefined = [];
        if (resourceData && resourceData.id > 0) {
            const validateBarItem = {
                important: false,
                text: Resources.EditResource,
                id: "k8s-validate-resource-command",
                onActivate: () => this._showValidateResourcePanel(resourceDetails!)
            };
            const deleteBarItem = {
                important: false,
                text: Resources.DeleteResource,
                id: "k8s-delete-resource-command",
                onActivate: () => this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss) => {
                    const deleteDialogProps = {
                        id: resourceData.id,
                        headerText: localeFormat(Resources.DeleteResourceDialogHeader, resourceData.name),
                        message: Resources.DeleteResourceDialogText,
                        doNotDismissOnOkClick: true,
                        onOkClick: (id: number, closeDialog?: () => void, setDialogError?: (errorMessage: React.ReactNode) => void) => this.context.pageContext.getRestClient<ITaskAgentRestClient>(TaskAgentClientName)
                            .deleteKubernetesResource(getTFSPageData(this.context.pageContext)!.project!.id, resourceData.environmentReference.id, resourceData.id)
                            .then(() => {
                                if (closeDialog) {
                                    closeDialog();
                                }
                                onDismiss();
                                this._navigateToResourcesListFunc(resourceData.environmentReference.id.toString(), resourceData.environmentReference.name)();
                            },
                                error => {
                                    if (setDialogError) {
                                        setDialogError(error.message || error);
                                    }
                                }),
                        onDismiss: onDismiss
                    }

                    return <WrappedComponent
                        wrappedType={"deleteEnvironmentResourceDialog"}
                        dependencies={["ms.vss-environments-web.environments-common"]}
                        wrappedProps={deleteDialogProps}
                    />;
                })
            };

            // show both commands in hidden mode for non-delete mode.
            if (resourceErrorType !== ResourceErrorType.Deleted) {
                headerBarItems.push(validateBarItem);

                headerBarItems.push(deleteBarItem);
            }
            else {
                // for delete show delete only
                headerBarItems.push({ ...deleteBarItem, important: true });
            }
        }

        return headerBarItems.length > 0 ? headerBarItems : undefined;
    }

    private get _contributionSource(): IVssContributionService {
        return this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    private _navigateToResourcesListFunc(environmentId: string, deletedResourceName?: string): () => void {
        const resourceUrlParams: { [key: string]: string } = { environmentId: environmentId };
        if (deletedResourceName) {
            resourceUrlParams["deletedResource"] = deletedResourceName;
        }

        const resourceUrl = createUrlForRoute(this.context.pageContext, "ms.vss-environments-web.environments-route-with-id", resourceUrlParams);
        return () => FastPageSwitch(this.context.pageContext, resourceUrl, true);
    }

    private _getContentReaderComponent = (props?: any): React.ReactNode => {
        const { className, contentClassName, options } = props || {} as any;
        const contentLanguage = options && options.language === "text/plain" ? "text/plain" : "application/x-yaml";

        if (contentLanguage === "text/plain") {
            return (
                <LogContentReader
                    className={className}
                    contentClassName={contentClassName}
                    contentProps={props}
                />
            );
        }

        return (
            <YamlReader
                className={className}
                contentClassName={contentClassName}
                contentLanguage={contentLanguage}
                contentProps={props}
                onEditorCreated={this._onEditorCreated}
            />
        );
    }

    private _loadDataFromServer = (queryObject: string, params?: { [key: string]: string }): Promise<IKubeSummaryData> => {
        const parameters: { [key: string]: string } = {};
        if (params) {
            Object.keys(params).forEach(key => {
                parameters[key] = params[key];
            });
        }

        parameters["queryobjects"] = queryObject;

        return this._contributionSource.getDataAsync<IKubeSummaryData>(this._dataProviderContributionId, parameters, true) as Promise<IKubeSummaryData>;
    }

    private _handleViewChanged = (viewTree: BreadcrumbItemType[]) => {
        const resourceDetails = this.context.pageContext.getService<IKubernetesDataService>("IKubernetesDataService").getKubernetesResourceData();
        const resource = resourceDetails && resourceDetails.resource;
        let items: BreadcrumbItemType[] = [];
        if (resource && resource.environmentReference && resource.environmentReference.name) {
            const environmentId = resource.environmentReference.id.toString();
            const environmentItem: BreadcrumbItemType = {
                id: `environment${environmentId}`,
                displayName: resource.environmentReference.name,
                url: createUrlForRoute(this.context.pageContext, "ms.vss-environments-web.environments-route-with-id", { environmentId: environmentId })
            };

            const resourceId = resource.id.toString();
            const resourceItem: BreadcrumbItemType = {
                id: `k8sResource${resourceId}`,
                displayName: resource.name,
                url: createUrlForRoute(this.context.pageContext, "ms.vss-environments-web.kubernetes-summary-default-route", { environmentId: environmentId, resourceId: resourceId })
            }

            items.push(environmentItem, resourceItem)
        }

        items.push(...viewTree);

        this.context.pageContext.getService<KubernetesBreadcrumbService>("KubernetesBreadcrumbService").setBreadcrumb(...items);
    }

    private _onEditorCreated = (editor: any) => {
        if (editor) {
            this._editor = editor;
        }
    }

    private _onResizeHandler = () => {
        if (this._editor) {
            this._editor.layout();
        }
    }

    private _markTTI = (): void => {
        this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService")
            .markTimeToInteractive("Environments", "Resource.Kubernetes.External.PageLoad");
    }

    private _getImageLocation = (image: KubeImage): string | undefined => {
        return ImageLocationUtility.getImageLocation(this.context.pageContext, image);
    }

    private _getPageHeaderData(resource?: KubernetesResource): IPageHeaderData {
        const namespace = resource && resource.namespace;
        const resourceName = resource && resource.name;
        const clusterName = resource && resource.clusterName;
        return {
            title: namespace || resourceName || Resources.KubernetesSummaryDefaultTitle,
            namespace: namespace || "",
            clusterName: clusterName || ""
        };
    }

    private _getClusterUrl(resource?: IKubernetesResourceDetails): string | undefined {
        let clusterUrl: string | undefined = undefined;
        if (resource && resource.kubernetesEndpoint) {
            const authType = resource.kubernetesEndpoint.data["authorizationType"];
            if (authType && authType === "AzureSubscription") {
                const tenantId = resource.kubernetesEndpoint.authorization.parameters["azureTenantId"];
                const clusterId = resource.kubernetesEndpoint.data["clusterId"];
                if (tenantId && clusterId) {
                    clusterUrl = `https://portal.azure.com/#@${tenantId}/resource${clusterId}`;
                }
            }
        }
        return clusterUrl;
    }

    private _editor?: any; /* monaco.editor.IEditor */
    private _dataProviderContributionId: string = "ms.vss-environments-web.kubernetes-summary-data-provider";
}

Components.add("kubeSummaryExternal", KubernetesContainerExternal);

// Creating a copy here instead of taking a dependency on environment-common, as that project is quite big, and doesn't make a lot of sense taking that dependency
// as this component is reachable through service endpoints, and also through a browser url navigation
function createUrlForRoute(pageContext: IVssPageContext, routeContributionId: string, queryParams: { [key: string]: string }): string {
    const curentRouteValues = getRouteValues(pageContext);

    let routeValues: { [key: string]: string } = { project: curentRouteValues.project, ...queryParams };
    if (curentRouteValues.team && !(queryParams["project"] && queryParams["project"] !== curentRouteValues.project)) {
        routeValues.team = curentRouteValues.team;
    }

    const constructedUrl = routeUrl(
        pageContext,
        routeContributionId,
        routeValues);

    return constructedUrl;
}